import { Box } from '@truepill/react-capsule'
import { EditHeader } from 'components/Card'
import { useFormContext } from 'react-hook-form'
import { Cell, CellValue, Row, RowLabel, PV1Card, TextInput, SelectInput } from '../../Card/PV1Card'
import type { PV1PatientCardEditFormSchemaType } from './PV1PatientCardEditForm.validator'

export const PV1PatientCardEditForm = ({
  rxPatientData,
  isHumanPatient = false,
  editHeaderActions,
  onSubmit,
}: {
  rxPatientData: any
  reviewPatientData: any
  isHumanPatient?: boolean
  editHeaderActions: any
  onSubmit: (data: any) => void
}) => {
  const {
    getValues,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<PV1PatientCardEditFormSchemaType>()

  return (
    <form onSubmit={onSubmit} data-testid="pv1-patient-edit-form">
      <PV1Card data-testid="PV1PatientCard" title={<EditHeader {...editHeaderActions} />}>
        <Row rowSpan={2} data-testid="patientName">
          <Cell $borderBottom>
            <RowLabel bold>Patient name::</RowLabel>
          </Cell>
          <Cell $paddingLeft $borderBottom $borderRight>
            <CellValue>{rxPatientData.name}</CellValue>
          </Cell>
          <Cell $paddingLeft $borderBottom>
            <TextInput
              helperText={errors?.patientName?.message || ''}
              state={errors.patientName ? 'error' : 'default'}
              {...register('patientName')}
            />
          </Cell>
        </Row>
        <Row rowSpan={2} data-testid="dob">
          <Cell $borderBottom>
            <RowLabel bold>Date of birth:</RowLabel>
          </Cell>
          <Cell $paddingLeft $borderBottom $borderRight>
            <CellValue>{rxPatientData.dob}</CellValue>
          </Cell>
          <Cell $paddingLeft $borderBottom>
            <TextInput
              helperText={errors?.dob?.message || ''}
              state={errors.dob ? 'error' : 'default'}
              {...register('dob')}
            />
          </Cell>
        </Row>
        <Row rowSpan={2} data-testid="gender">
          <Cell $borderBottom>
            <RowLabel bold>Sex:</RowLabel>
          </Cell>
          <Cell $paddingLeft $borderBottom $borderRight>
            <CellValue>{rxPatientData.gender}</CellValue>
          </Cell>
          <Cell $paddingLeft $borderBottom>
            <SelectInput
              value={getValues().gender || ''}
              options={['Female', 'Male']}
              variant="small"
              helperText={errors?.gender?.message || ''}
              state={errors.gender ? 'error' : 'default'}
              {...register('gender')}
              onChange={(value: any) => {
                if (value === getValues().gender) return

                if (!value) {
                  setValue('gender', '', { shouldValidate: true })
                } else {
                  setValue('gender', value, { shouldValidate: true })
                }
              }}
            />
          </Cell>
        </Row>
        {!isHumanPatient && (
          <>
            <Row rowSpan={2} data-testid="species">
              <Cell $borderBottom>
                <RowLabel bold>Species:</RowLabel>
              </Cell>
              <Cell $paddingLeft $borderBottom $borderRight>
                <CellValue>{rxPatientData.species}</CellValue>
              </Cell>
              <Cell $paddingLeft $borderBottom>
                <SelectInput
                  value={getValues().species || ''}
                  options={['Human', 'Canine']}
                  variant="small"
                  helperText={errors?.species?.message || ''}
                  state={errors.species ? 'error' : 'default'}
                  {...register('species')}
                  onChange={(value: any) => {
                    if (value === getValues().species) return

                    if (!value) {
                      setValue('species', '', { shouldValidate: true })
                    } else {
                      setValue('species', value, { shouldValidate: true })
                    }
                  }}
                />
              </Cell>
            </Row>
            <Row rowSpan={2} data-testid="weight">
              <Cell $borderBottom>
                <RowLabel bold>Weight:</RowLabel>
              </Cell>
              <Cell $paddingLeft $borderBottom $borderRight>
                <CellValue>{rxPatientData.weight}</CellValue>
              </Cell>
              <Cell $paddingLeft $borderBottom>
                <TextInput
                  helperText={errors?.weight?.message || ''}
                  state={errors.weight ? 'error' : 'default'}
                  {...register('weight')}
                />
              </Cell>
            </Row>
            <Row rowSpan={2} data-testid="guardian">
              <Cell $borderBottom>
                <RowLabel bold>Guardian:</RowLabel>
              </Cell>
              <Cell $paddingLeft $borderBottom $borderRight>
                <CellValue>{rxPatientData.guardian}</CellValue>
              </Cell>
              <Cell $paddingLeft $borderBottom>
                <TextInput
                  helperText={errors?.guardian?.message || ''}
                  state={errors.guardian ? 'error' : 'default'}
                  {...register('guardian')}
                />
              </Cell>
            </Row>
          </>
        )}
        <Row rowSpan={2} data-testid="address1">
          <Cell $borderBottom>
            <RowLabel bold>Adress 1:</RowLabel>
          </Cell>
          <Cell $paddingLeft $borderBottom $borderRight>
            <CellValue>{rxPatientData?.address.street1}</CellValue>
          </Cell>
          <Cell $paddingLeft $borderBottom>
            <TextInput
              helperText={errors?.address1?.message || ''}
              state={errors.address1 ? 'error' : 'default'}
              {...register('address1')}
            />
          </Cell>
        </Row>
        <Row rowSpan={2} data-testid="address2">
          <Cell $borderBottom>
            <RowLabel bold>Adress 2:</RowLabel>
          </Cell>
          <Cell $paddingLeft $borderBottom $borderRight>
            <CellValue>{rxPatientData.address.street2}</CellValue>
          </Cell>
          <Cell $paddingLeft $borderBottom>
            <TextInput
              helperText={errors?.address2?.message || ''}
              state={errors.address2 ? 'error' : 'default'}
              {...register('address2')}
            />
          </Cell>
        </Row>
        <Row rowSpan={2} data-testid="city">
          <Cell $borderBottom>
            <RowLabel bold>City:</RowLabel>
          </Cell>
          <Cell $paddingLeft $borderBottom $borderRight>
            <CellValue>{rxPatientData.address.city}</CellValue>
          </Cell>
          <Cell $paddingLeft $borderBottom>
            <TextInput
              helperText={errors?.city?.message || ''}
              state={errors.city ? 'error' : 'default'}
              {...register('city')}
            />
          </Cell>
        </Row>
        <Row rowSpan={2} data-testid="city">
          <Cell $borderBottom>
            <RowLabel bold>State/Zip:</RowLabel>
          </Cell>
          <Cell $paddingLeft $borderBottom $borderRight>
            <CellValue>
              {rxPatientData.address.state}/{rxPatientData.address.zip}
            </CellValue>
          </Cell>
          <Cell $paddingLeft $borderBottom>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: '$xs',
              }}
            >
              <TextInput
                helperText={errors?.zip?.message || ''}
                state={errors.zip ? 'error' : 'default'}
                {...register('zip')}
              />
              <SelectInput
                value={getValues().state || ''}
                options={['NY', 'CA']}
                variant="small"
                helperText={errors?.state?.message || ''}
                state={errors.state ? 'error' : 'default'}
                {...register('state')}
                onChange={(value: any) => {
                  if (value === getValues().state) return

                  if (!value) {
                    setValue('state', '', { shouldValidate: true })
                  } else {
                    setValue('state', value, { shouldValidate: true })
                  }
                }}
              />
            </Box>
          </Cell>
        </Row>
        <Row rowSpan={2} data-testid="phone">
          <Cell>
            <RowLabel bold>Phone:</RowLabel>
          </Cell>
          <Cell $paddingLeft $borderRight>
            <CellValue>{rxPatientData.phone}</CellValue>
          </Cell>
          <Cell $paddingLeft>
            <TextInput
              helperText={errors?.phone?.message || ''}
              state={errors.phone ? 'error' : 'default'}
              {...register('phone')}
            />
          </Cell>
        </Row>
      </PV1Card>
    </form>
  )
}
